import React, { useMemo, useState } from "react";
import ReportStepFrame from "components/report/ReportStepFrame";
import {
  TooltipGroup,
  useTooltipTrigger,
} from "components/report/useTooltipTrigger";
import styles from "./ScoreRangeStep.module.scss";

/** How a band's boundary marker reads. The caller decides; scoring meaning
 * is not this component's to invent. */
export type ScoreBandTone =
  | "critical"
  | "low"
  | "moderate"
  | "neutral"
  | "good";

export interface ScoreBand {
  /**
   * The band's stable identifier, which its label is not. Matched against
   * `currentBandKey` to decide which band the score is in.
   */
  key?: string;
  /** Lower bound, on the same scale as the score. */
  from: number;
  /** Upper bound, on the same scale as the score. */
  to: number;
  /** What the band is called. Copy, supplied by the caller. */
  label: string;
  /** Tone of the boundary marker at `from`. Left neutral when omitted. */
  tone?: ScoreBandTone;
  /**
   * How wide the band's column is, relative to the others. The frames do not
   * draw the bands to scale: a narrow band would leave its name no room. A
   * score is placed within its own band's column, in proportion to where it
   * falls between that band's bounds. Every band needs one for any to apply;
   * otherwise each column spans its score range.
   */
  width?: number;
}

export interface ScoreRangeStepProps {
  /**
   * The sentence naming the band the learner landed in. A `{band}` token is
   * replaced by the band pill, so the catalogue keeps the whole sentence and
   * a translator can move the pill where their grammar needs it. Without the
   * token the pill follows the sentence.
   */
  heading: string;
  /** The band pill's text, e.g. the caller's word for the band reached. */
  bandLabel?: string;
  /**
   * The `key` of the band the score belongs to, as the server resolved it.
   * Which band a score is in is the server's verdict, and the pill already
   * reads it, so the axis highlights the same band rather than deciding for
   * itself from the bounds (a score sitting exactly on a threshold belongs
   * to two bands by the bounds alone, and the two elements then disagree).
   */
  currentBandKey?: string;
  /**
   * What each band means, by band `key`, shown when the learner hovers,
   * focuses or taps the pill or that band's name in the chart header.
   * Learners tell us they do not know what "Strong" is, and the answer is
   * copy, not a rule this component knows. A band with no entry keeps a
   * plain label with nothing to open; the pill reads the entry for
   * `currentBandKey`.
   */
  bandMeanings?: Record<string, string>;
  /** The bands, low to high. Their bounds set the scale. */
  bands: ScoreBand[];
  /** Where the learner sits. What the marker reads out. */
  value: number;
  /**
   * The same score expressed on the bands' own scale, for when that is not
   * the scale `value` is on. The server states its bands on a normalised
   * range, and a score carries its own bounds, so placing one against the
   * other takes both numbers: the one the learner recognises, and the one
   * that says where it sits. Absent, `value` is taken to be on the bands'
   * scale already.
   */
  valueOnScale?: number;
  /**
   * Which way the bands run. `horizontal` is the built frame; `vertical`
   * stacks them highest-first, which a reviewer prefers because a
   * left-to-right scale reads as a timeline. Narrow viewports use vertical
   * either way — six band labels cannot sit side by side.
   */
  orientation?: "horizontal" | "vertical";
  /** Accessible name for the scale, when the caller has wording for one. */
  scaleLabel?: string;
  /**
   * The word under the marker's number, naming what it is. Copy, so the
   * caller owns it: the frames read "82" over "score".
   */
  scoreCaption?: string;
  /**
   * Told the band's `key` each time the learner opens what a band means,
   * from the pill or from the band's name in the chart header.
   */
  onBandOpen?: (key: string) => void;
}

interface Column {
  /** Where the band's column starts along the scale, as a percentage. */
  start: number;
  /** How much of the scale the column takes, as a percentage. */
  span: number;
}

const rounded = (percent: number) => Number(percent.toFixed(2));

/**
 * Each band's column along the scale, and where a value lands on it.
 *
 * A value is placed piecewise: within the column of the band it falls in, in
 * proportion to where it sits between that band's bounds. With the columns
 * sized by their score ranges that is the plain linear scale; with the widths
 * the caller gives, it keeps the marker, the boundaries and the colours on
 * the right score however wide each column is drawn. A value outside the
 * bands is clamped to the scale's ends rather than drawn off it.
 */
const scaleOf = (bands: ScoreBand[]) => {
  const weights = bands.every((band) => (band.width ?? 0) > 0)
    ? bands.map((band) => band.width as number)
    : bands.map((band) => Math.max(0, band.to - band.from));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let reached = 0;
  const columns: Column[] = weights.map((weight) => {
    const span = total > 0 ? (weight / total) * 100 : 0;
    const column = { start: reached, span };
    reached += span;
    return column;
  });

  const positionOf = (value: number): number => {
    if (bands.length === 0 || total <= 0) return 0;
    if (value <= bands[0].from) return 0;
    if (value >= bands[bands.length - 1].to) return 100;
    // The first band whose top the value has not passed. Bands meet at their
    // bounds, so a value on a boundary takes the lower band's top, which is
    // the same point as the next band's start.
    const index = bands.findIndex((band) => value <= band.to);
    const band = bands[index];
    const { start, span } = columns[index];
    const range = band.to - band.from;
    const within = range > 0 ? Math.max(0, (value - band.from) / range) : 0;
    return rounded(start + within * span);
  };

  return { columns, positionOf };
};

const BAND_TOKEN = "{band}";

/** The band pill as the trigger for what the band means. */
const BandPill: React.FC<{
  label: string;
  meaning: string;
  tone?: ScoreBandTone;
  onOpen?: () => void;
}> = ({ label, meaning, tone, onOpen }) => {
  const { open, tooltipId, tooltipRef, rootProps, buttonProps } =
    useTooltipTrigger(onOpen);

  return (
    <span {...rootProps} className={styles["pill-trigger"]}>
      <button
        {...buttonProps}
        className={styles.pill}
        data-testid="score-range-pill"
      >
        <span>{label}</span>
        <span className={styles.dot} data-tone={tone} aria-hidden="true" />
      </button>
      {/* Hidden rather than unmounted, so the button keeps its description
          for a screen reader whether or not the tooltip is showing. */}
      <span
        ref={tooltipRef}
        role="tooltip"
        id={tooltipId}
        className={styles.tooltip}
        hidden={!open}
      >
        {meaning}
      </span>
    </span>
  );
};

/**
 * A band's name in the chart header as the trigger for what it means, so a
 * learner can read every band and not only the one they landed in.
 *
 * `align` says which edge of the label the tooltip hangs from. The outermost
 * bands sit against the card's edges, where a centred tooltip would spill
 * past the chart, so they anchor to their outer side instead.
 */
const BandLabel: React.FC<{
  label: string;
  meaning: string;
  align: "start" | "center" | "end";
  onOpen?: () => void;
}> = ({ label, meaning, align, onOpen }) => {
  const { open, tooltipId, tooltipRef, rootProps, buttonProps } =
    useTooltipTrigger(onOpen);

  return (
    <span {...rootProps} className={styles["band-trigger"]}>
      <button
        {...buttonProps}
        className={`${styles["band-label"]} ${styles["band-button"]}`}
      >
        {label}
      </button>
      <span
        ref={tooltipRef}
        role="tooltip"
        id={tooltipId}
        className={`${styles.tooltip} ${styles["band-tooltip"]}`}
        data-align={align}
        hidden={!open}
      >
        {meaning}
      </span>
    </span>
  );
};

/**
 * Which custom property holds each tone's colour. The axis is drawn as one
 * flat block of colour per band, in the colour of the dot that opens it, with
 * a hard edge where the next band starts: a blend made neighbouring bands
 * hard to tell apart. The edges are the bands' own positions, which arrive
 * as data, so the ramp is composed here and the colours stay in the
 * stylesheet, referenced rather than duplicated.
 */
const TONE_VARIABLES: Record<ScoreBandTone, string> = {
  critical: "--tone-critical",
  low: "--tone-low",
  moderate: "--tone-moderate",
  neutral: "--tone-neutral",
  good: "--tone-good",
};

/**
 * The step placing the learner's score against the bands, so the number
 * means something.
 *
 * The design has three versions of this screen and nobody has picked one.
 * They are not three arrangements — all three draw the same horizontal
 * scale, and differ by adding a band explainer and a previous-assessment
 * marker. The arrangement a reviewer actually asked for, vertical with the
 * highest band on top, is not drawn anywhere. So the geometry here is one
 * axis driven by a `--position` custom property per tick: swapping
 * arrangements is a stylesheet concern, not a rebuild.
 */
const ScoreRangeStep: React.FC<ScoreRangeStepProps> = ({
  heading,
  bandLabel,
  currentBandKey,
  bandMeanings,
  bands,
  value,
  valueOnScale,
  orientation = "horizontal",
  scaleLabel,
  scoreCaption,
  onBandOpen,
}) => {
  const max = bands.length > 0 ? bands[bands.length - 1].to : 0;
  const { columns, positionOf } = scaleOf(bands);
  // Everything positional reads this rather than `value`, so a score on a
  // different scale lands in the right place on the axis.
  const placed = valueOnScale ?? value;
  // Fallback only, for a scale and a label out of sync: with no band
  // carrying the server's key, a bounds match is better than highlighting
  // nothing. Both bounds are inclusive, so a boundary value lands in the
  // lower of the two bands it matches.
  const bandContaining = bands.find(
    (band) => placed >= band.from && placed <= band.to
  );
  const markerBand =
    bands.find(
      (band) => band.key !== undefined && band.key === currentBandKey
    ) ?? bandContaining;

  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const tooltipGroup = useMemo(
    () => ({ active: activeTooltip, claim: setActiveTooltip }),
    [activeTooltip]
  );
  const meaningOf = (key?: string) =>
    key === undefined ? undefined : bandMeanings?.[key];
  const bandMeaning = meaningOf(currentBandKey);

  const pill = !bandLabel ? null : bandMeaning ? (
    <BandPill
      label={bandLabel}
      meaning={bandMeaning}
      tone={markerBand?.tone}
      onOpen={
        onBandOpen && currentBandKey
          ? () => onBandOpen(currentBandKey)
          : undefined
      }
    />
  ) : (
    <span className={styles.pill} data-testid="score-range-pill">
      <span>{bandLabel}</span>
      <span
        className={styles.dot}
        data-tone={markerBand?.tone}
        aria-hidden="true"
      />
    </span>
  );

  // Without the token the pill follows the sentence, and needs the space the
  // token would otherwise have carried — otherwise the heading's accessible
  // name runs the two together.
  const [before, after] = heading.includes(BAND_TOKEN)
    ? heading.split(BAND_TOKEN)
    : [pill ? `${heading} ` : heading, ""];

  // The boundary values the design prints under the axis: every band's start,
  // plus the top of the last one.
  const ticks = bands.map((band, index) => ({
    at: band.from,
    tone: band.tone,
    position: rounded(columns[index].start),
  }));
  if (bands.length > 0) {
    ticks.push({
      at: max,
      tone: bands[bands.length - 1].tone,
      position: 100,
    });
  }

  // Each band's colour is stopped at both its edges, which is what makes the
  // edges hard rather than blended. An unrecognised tone takes the neutral
  // default rather than dropping its stops, which would silently stretch its
  // neighbour across the gap.
  const axisRamp = bands
    .map((band, index) => {
      const variable = band.tone ? TONE_VARIABLES[band.tone] : undefined;
      const colour = `var(${variable ?? "--tone-default"})`;
      const { start, span } = columns[index];
      return `${colour} ${rounded(start)}%, ${colour} ${rounded(
        start + span
      )}%`;
    })
    .join(", ");

  return (
    <ReportStepFrame>
      <TooltipGroup.Provider value={tooltipGroup}>
        <div className={styles.step} data-testid="score-range-step">
          <h2 className={styles.heading}>
            {before}
            {pill}
            {after}
          </h2>

          {bands.length > 0 && (
            <div
              className={styles.scale}
              data-orientation={orientation}
              data-testid="score-range-scale"
              style={{ "--axis-ramp": axisRamp } as React.CSSProperties}
            >
              {/* The header row sits outside the image role: its names open
                tooltips, and an image's children are presentational, so a
                button inside one is unreachable to a screen reader. */}
              <ul className={styles.bands}>
                {bands.map((band, index) => {
                  const meaning = meaningOf(band.key);
                  const align =
                    index === 0
                      ? "start"
                      : index === bands.length - 1
                      ? "end"
                      : "center";
                  return (
                    <li
                      key={`${band.label}-${band.from}`}
                      className={styles.band}
                      data-testid="score-range-band"
                      // The frames pick out the band the score landed in, so the
                      // eye finds it before reading any of the labels.
                      data-current={band === markerBand}
                      style={
                        {
                          "--start": rounded(columns[index].start),
                          "--span": rounded(columns[index].span),
                        } as React.CSSProperties
                      }
                    >
                      {meaning ? (
                        <BandLabel
                          label={band.label}
                          meaning={meaning}
                          align={align}
                          onOpen={
                            onBandOpen && band.key !== undefined
                              ? () => onBandOpen(band.key as string)
                              : undefined
                          }
                        />
                      ) : (
                        <span className={styles["band-label"]}>
                          {band.label}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>

              {/* Ticks and the marker share one track, so both are positioned
                against the same span of the axis rather than against the
                card, whichever way the axis runs. The plotted scale is the
                image, named for assistive technology as one reading. */}
              <div
                className={styles.track}
                data-testid="score-range-plot"
                role="img"
                aria-label={scaleLabel}
              >
                <ul className={styles.ticks}>
                  {ticks.map((tick) => (
                    <li
                      key={tick.at}
                      className={styles.tick}
                      data-testid="score-range-tick"
                      style={
                        {
                          "--position": tick.position,
                        } as React.CSSProperties
                      }
                    >
                      <span
                        className={styles.dot}
                        data-tone={tick.tone}
                        aria-hidden="true"
                      />
                      <span className={styles["tick-value"]}>{tick.at}</span>
                    </li>
                  ))}
                </ul>

                <p
                  className={styles.marker}
                  data-testid="score-range-marker"
                  style={
                    {
                      "--position": positionOf(placed),
                    } as React.CSSProperties
                  }
                >
                  <span className={styles["marker-value"]}>{value}</span>
                  {scoreCaption && (
                    <span className={styles["marker-caption"]}>
                      {scoreCaption}
                    </span>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </TooltipGroup.Provider>
    </ReportStepFrame>
  );
};

export default ScoreRangeStep;
